import { Button, ModalOverlay, ModalDialog } from '@flos/react-ui';
import { useState } from 'react';
import { useRemoveCoverable } from '../../recoil/hooks/useRemoveCoverable';
import { useHippoText } from '../../hooks/useHippoText';
import { allCoverablesAtom } from '../../recoil/atoms/allCoverablesAtom';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import {
    isOnsaCoverable,
    numberOfCoverablesInShoppingBasket,
    removeCoverableFromAllCoverablesDTO,
    removeCoverableFromAllCoverablesOnsaDTO,
    removeCoverableFromProductStatusList,
    sameCoverableAndProductStatus
} from '../../pages/ShoppingBasketPage/utils/ShoppingBasketUtil';
import { useRecoilValue, useRecoilState, useSetRecoilState, useResetRecoilState } from 'recoil';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import { onsaProductStatusListAtom } from '../../recoil/atoms/onsaProductStatusListAtom';
import { getOnsaCurrentProductSelector } from '../../recoil/selectors/getOnsaCurrentProductSelector';
import { onsaProgressAtom } from '../../recoil/atoms/onsaProgressAtom';
import { shoppingBasketOverlayAtom } from '../../recoil/atoms/shoppingBasketOverlayAtom';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import { trackRemoveProduct } from '../../utils/trackingUtil';
import { refreshCoverablesSelectorAtom } from '../../recoil/atoms/refreshCoverablesSelectorAtom';
import DOMPurify from 'dompurify';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { DRAFT } from '../../recoil/types/types';
import { currentPackageGroupAtom } from '../../recoil/atoms/currentPackageGroupAtom';

const RemoveModal = ({
    coverable,
    title,
    description,
    buttonText,
    isRemoveAndContinueModal = false,
    showModal,
    showCloseButton = false,
    isCalledFromResumeOnsa = false,
    closeModal,
    setOpenErrorModal
}: {
    coverable: CoverableDTO;
    title?: string;
    description?: string;
    buttonText?: string;
    isRemoveAndContinueModal?: boolean;
    showModal: boolean;
    showCloseButton?: boolean;
    isCalledFromResumeOnsa?: boolean;
    closeModal: () => void;
    setOpenErrorModal?: React.Dispatch<React.SetStateAction<boolean>>;
}): React.JSX.Element => {
    const [openRemoveModal, setOpenRemoveModal] = useState(showModal);
    const { removeCoverable } = useRemoveCoverable();
    const getHippoText = useHippoText();
    const [allCoverables, setAllCoverables] = useRecoilState(allCoverablesAtom);
    const setRefreshCoverablesSelector = useSetRecoilState(refreshCoverablesSelectorAtom);
    const [allCoverablesOnsa, setAllCoverablesOnsa] = useRecoilState(allCoverablesOnsaAtom);
    const [productStatusList, setProductStatusList] = useRecoilState(onsaProductStatusListAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { wizardGotoStep } = useWizardNavigation();
    const resetOnsaProgress = useResetRecoilState(onsaProgressAtom);
    const setShoppingBasketOverlayInfo = useSetRecoilState(shoppingBasketOverlayAtom);
    const [jobData, setJobData] = useRecoilState(jobAtom);
    const isDraftStatus = jobData.jobStatus === DRAFT;
    const [isLoading, setIsLoading] = useState(false);
    const resetCurrentPackageGroup = useResetRecoilState(currentPackageGroupAtom);

    const modalTitle =
        title ??
        (isRemoveAndContinueModal
            ? getHippoText('DSS-REMOVE-CONTINUE-MODAL-HEADER')
            : getHippoText('DSS-SHOPPING-BASKET-REMOVE-NEW-COVERABLE-MODAL-HEADER'));
    const modalDescription =
        description ??
        (isRemoveAndContinueModal
            ? getHippoText('DSS-REMOVE-CONTINUE-MODAL-SUBHEADER', {
                  coverable_information: coverable.name ?? ''
              })
            : getHippoText('DSS-SHOPPING-BASKET-REMOVE-NEW-COVERABLE-MODAL-DESCRIPTION', {
                  coverable: (coverable.flavourName ?? '').toLowerCase()
              }));
    const primaryButtonText = isRemoveAndContinueModal
        ? getHippoText('DSS-REMOVE-CONTINUE-MODAL-BUTTON')
        : getHippoText('DSS-SHOPPING-BASKET-REMOVE-NEW-COVERABLE-MODAL-UNDO');

    const resetJobStatusToDraft = (): void => {
        setJobData((prevData) => ({
            ...prevData,
            jobStatus: DRAFT
        }));
    };

    const removeAddedCoverable = async (): Promise<void> => {
        if (coverable.status === 'Added') {
            trackRemoveProduct(coverable);
            await removeCoverable(coverable.lobCode ?? '', coverable.fixedId ?? '');

            if (!isDraftStatus) {
                resetJobStatusToDraft();
            }

            const updatedAllCoverables = removeCoverableFromAllCoverablesDTO(
                allCoverables,
                coverable
            );
            if (updatedAllCoverables) {
                setAllCoverables(updatedAllCoverables);
            }
        } else if (isOnsaCoverable(coverable, productStatusList)) {
            await removeCoverable(coverable.lobCode ?? '', coverable.fixedId ?? '');

            if (!isDraftStatus) {
                resetJobStatusToDraft();
            }

            const updatedAllCoverablesOnsa = removeCoverableFromAllCoverablesOnsaDTO(
                allCoverablesOnsa,
                coverable
            );
            const updatedProductStatusList = removeCoverableFromProductStatusList(
                productStatusList,
                coverable
            );
            if (updatedAllCoverablesOnsa && updatedProductStatusList) {
                setAllCoverablesOnsa(updatedAllCoverablesOnsa);
                setProductStatusList(updatedProductStatusList);
                if (sameCoverableAndProductStatus(coverable, currentProduct)) {
                    resetOnsaProgress();
                    resetCurrentPackageGroup();
                    const hasUncalculatedProduct = Object.values(updatedProductStatusList).some(
                        (flavour) => flavour.some((cov) => !cov.calculated)
                    );
                    const coverablesLeft = numberOfCoverablesInShoppingBasket(
                        allCoverables,
                        updatedAllCoverablesOnsa
                    );
                    if (isCalledFromResumeOnsa) return;
                    if (hasUncalculatedProduct) {
                        wizardGotoStep('CoverableInformationPage');
                        setShoppingBasketOverlayInfo({
                            isButtonDisplayed: false,
                            isOverlayOpen: false
                        });
                    } else if (coverablesLeft !== 0) {
                        setRefreshCoverablesSelector({});
                        wizardGotoStep('ShoppingBasketPage');
                        setShoppingBasketOverlayInfo({
                            isButtonDisplayed: false,
                            isOverlayOpen: false
                        });
                    } else if (coverablesLeft === 0) {
                        setShoppingBasketOverlayInfo({
                            isButtonDisplayed: false,
                            isOverlayOpen: true
                        });
                    }
                }
            }
        }
    };

    return (
        <div id="remove-modal" className="u-color-primary-2-dark">
            <ModalOverlay isOpen={openRemoveModal}>
                <ModalDialog closeIconHandler={closeModal}>
                    <div className="u-spacing-inset-l">
                        <h4 className="u-spacing-stack-m">{modalTitle}</h4>
                        <p
                            className="u-spacing-stack-m"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(modalDescription)
                            }}
                        />
                        <div>
                            {showCloseButton && (
                                <Button
                                    disabled={isLoading}
                                    theme={'ghost'}
                                    onClick={() => {
                                        closeModal();
                                    }}
                                >
                                    Fortryd
                                </Button>
                            )}
                            <Button
                                disabled={isLoading}
                                onClick={() => {
                                    setIsLoading(true);
                                    void removeAddedCoverable().then(() => {
                                        setIsLoading(false);
                                        closeModal();
                                    });
                                }}
                            >
                                {buttonText ?? primaryButtonText}
                            </Button>
                            {!isRemoveAndContinueModal && (
                                <Button
                                    disabled={isLoading}
                                    theme={'ghost'}
                                    onClick={() => {
                                        setOpenRemoveModal(false);
                                        setOpenErrorModal ? setOpenErrorModal(true) : closeModal();
                                    }}
                                >
                                    {getHippoText(
                                        'DSS-SHOPPING-BASKET-REMOVE-NEW-COVERABLE-MODAL-CANCEL'
                                    )}
                                </Button>
                            )}
                        </div>
                    </div>
                </ModalDialog>
            </ModalOverlay>
        </div>
    );
};

export default RemoveModal;
